import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatRelative,
  isPast,
} from '../../lib/format';
import { listAttendants, listBusinesses, type BusinessAction } from '../../lib/resources';
import type { Stage, StageSummary } from '../../types';
import { Button, StatusBadge } from '../ui';

const PAGE = 50;

/**
 * Visão Lista: carrossel de etapas (com a contagem do resumo) e a tabela com
 * seleção. A seleção vira alvo das ações em lote por id.
 */
export function BusinessListView({
  stages,
  summary,
  filter,
  stageId,
  onStage,
  onBulk,
}: {
  stages: Stage[];
  summary: StageSummary[];
  filter: string;
  stageId: string | null;
  onStage: (stageId: string | null) => void;
  onBulk: (action: BusinessAction, ids: string[]) => void;
}) {
  // A seleção vale para a combinação etapa + filtro; mudou, começa vazia.
  const selectionKey = `${stageId ?? 'todas'}|${filter}`;
  const [selection, setSelection] = useState<{ key: string; ids: Set<string> }>({
    key: selectionKey,
    ids: new Set(),
  });
  const selected = selection.key === selectionKey ? selection.ids : new Set<string>();
  const setSelected = (ids: Set<string>) => setSelection({ key: selectionKey, ids });

  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });
  const pipelineId = stages[0]?.pipelineId;
  const query = useInfiniteQuery({
    queryKey: ['kanban', 'list', pipelineId, stageId, filter],
    queryFn: ({ pageParam }) =>
      listBusinesses(
        stageId
          ? { stageId, skip: pageParam, take: PAGE }
          : { pipelineId, skip: pageParam, take: PAGE },
        filter,
      ),
    initialPageParam: 0,
    enabled: Boolean(pipelineId),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < last.count ? loaded : undefined;
    },
  });
  const rows = (query.data?.pages ?? []).flatMap((page) => page.data);
  const count = query.data?.pages[0]?.count ?? 0;
  const stageName = (id: string) => stages.find((stage) => stage.id === id)?.name ?? '—';
  const totalAll = summary.reduce((sum, item) => sum + item.businessesCount, 0);
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex gap-2 overflow-x-auto pb-1">
        <StageChip
          label="Todas"
          count={totalAll}
          active={stageId === null}
          onClick={() => onStage(null)}
        />
        {stages.map((stage) => (
          <StageChip
            key={stage.id}
            label={stage.name}
            color={stage.color}
            count={summary.find((item) => item.stageId === stage.id)?.businessesCount ?? 0}
            active={stageId === stage.id}
            onClick={() => onStage(stage.id)}
          />
        ))}
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white">
          <span className="mr-2">{selected.size} selecionado(s)</span>
          {(
            [
              ['move', 'Mover'],
              ['win', 'Ganhar'],
              ['lose', 'Perder'],
              ['restore', 'Restaurar status'],
              ['delete', 'Excluir'],
            ] as Array<[BusinessAction, string]>
          ).map(([action, label]) => (
            <button
              key={action}
              type="button"
              onClick={() => onBulk(action, [...selected])}
              className="rounded-md bg-white/10 px-2.5 py-1 text-xs hover:bg-white/20"
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-white/70 hover:text-white"
          >
            Limpar seleção
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="sticky top-0 border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  aria-label="Selecionar todos os carregados"
                  checked={allSelected}
                  onChange={() =>
                    setSelected(allSelected ? new Set() : new Set(rows.map((row) => row.id)))
                  }
                />
              </th>
              <th className="px-3 py-2">Negócio</th>
              <th className="px-3 py-2">Etapa</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Valor</th>
              <th className="px-3 py-2">Atendente</th>
              <th className="px-3 py-2">Próxima atividade</th>
              <th className="px-3 py-2">Criado</th>
              <th className="px-3 py-2">Movido</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => {
              const next = row.nextActivity;
              const overdue = isPast(next?.startDate);
              return (
                <tr key={row.id} className={selected.has(row.id) ? 'bg-slate-50' : ''}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Selecionar #${row.code}`}
                      checked={selected.has(row.id)}
                      onChange={() => toggle(row.id)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Link to={`/negocios/${row.id}`} className="font-medium hover:underline">
                      {row.lead?.name ?? 'Lead'}
                    </Link>
                    <span className="ml-1 text-xs text-slate-400">#{row.code}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{stageName(row.stageId)}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="px-3 py-2 text-right">{formatMoney(row.total)}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {attendants.data?.data.find((item) => item.userId === row.attendantId)
                      ?.name ?? '—'}
                  </td>
                  <td
                    className={`px-3 py-2 text-xs ${overdue ? 'font-medium text-red-600' : 'text-slate-500'}`}
                  >
                    {next ? `${next.title} · ${formatDateTime(next.startDate)}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {formatDate(row.createdAt)}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {formatRelative(row.lastMovedAt)}
                  </td>
                </tr>
              );
            })}
            {!query.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-xs text-slate-400">
                  Nenhum negócio com estes filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>
          {rows.length} de {count} negócio(s)
        </span>
        {query.hasNextPage && (
          <Button onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
            {query.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
          </Button>
        )}
      </div>
    </div>
  );
}

function StageChip({
  label,
  count,
  color,
  active,
  onClick,
}: {
  label: string;
  count: number;
  color?: string | null;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
        active
          ? 'border-slate-900 bg-slate-900 text-white'
          : 'border-slate-300 bg-white text-slate-700'
      }`}
    >
      {color && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />}
      {label}
      <span className={`text-xs ${active ? 'text-white/70' : 'text-slate-400'}`}>{count}</span>
    </button>
  );
}
